'use client'

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
    type ReactNode,
} from 'react'

const STORAGE_KEY = 'chat-dock:open'

interface ChatDockContextValue {
    /** A lista de conversas está acoplada ao menu. */
    isDockOpen: boolean
    openDock: () => void
    /**
     * `remember: false` fecha SEM gravar a preferência: serve a quem fechou de
     * passagem (ao navegar para outra tela a partir da conversa), e não porque
     * decidiu trabalhar sem o dock. O padrão grava.
     */
    closeDock: (options?: { remember?: boolean }) => void
    toggleDock: () => void
    /** Conversa sobreposta à tela atual, ou `null`. */
    openChatId: number | null
    openChat: (chatId: number) => void
    closeChat: () => void
}

const ChatDockContext = createContext<ChatDockContextValue | null>(null)

/**
 * O dock de atendimento: a lista de conversas encostada no menu, disponível em
 * qualquer tela do sistema.
 *
 * O estado mora aqui, e não na rota, porque é isso que faz a conversa abrir POR
 * CIMA do que a pessoa está fazendo sem tirá-la de lá: nada navega, então fechar
 * a conversa devolve a tela intacta — com o formulário preenchido, o filtro
 * aplicado e a rolagem onde estava. Guardar isso na URL traria de volta todos os
 * problemas que a sobreposição existe para evitar.
 *
 * Só o dock é lembrado entre sessões. A conversa aberta não: reabrir o navegador
 * no meio de um atendimento de ontem seria surpresa, não conveniência.
 */
export function ChatDockProvider({ children }: { children: ReactNode }) {
    const [isDockOpen, setIsDockOpen] = useState(false)
    const [openChatId, setOpenChatId] = useState<number | null>(null)

    // Leitura adiada para depois da montagem: o acessador pode lançar (janela
    // anônima, dados do site bloqueados) e não pode derrubar o painel inteiro.
    useEffect(() => {
        try {
            setIsDockOpen(window.localStorage.getItem(STORAGE_KEY) === '1')
        } catch {
            // Sem memória do navegador o dock apenas começa fechado.
        }
    }, [])

    const persist = useCallback((open: boolean) => {
        try {
            window.localStorage.setItem(STORAGE_KEY, open ? '1' : '0')
        } catch {
            // A preferência se perde ao recarregar; o dock segue funcionando.
        }
    }, [])

    const openDock = useCallback(() => {
        setIsDockOpen(true)
        persist(true)
    }, [persist])

    const closeDock = useCallback(
        (options: { remember?: boolean } = {}) => {
            setIsDockOpen(false)
            // Fechar a lista fecha a conversa junto: deixar a sobreposição sem a
            // fila que a abriu é um beco — não haveria como voltar para a lista.
            setOpenChatId(null)
            if (options.remember !== false) persist(false)
        },
        [persist],
    )

    const toggleDock = useCallback(() => {
        setIsDockOpen(prev => {
            const next = !prev
            if (!next) setOpenChatId(null)
            persist(next)
            return next
        })
    }, [persist])

    const openChat = useCallback((chatId: number) => setOpenChatId(chatId), [])
    const closeChat = useCallback(() => setOpenChatId(null), [])

    const value = useMemo(
        () => ({
            isDockOpen,
            openDock,
            closeDock,
            toggleDock,
            openChatId,
            openChat,
            closeChat,
        }),
        [isDockOpen, openDock, closeDock, toggleDock, openChatId, openChat, closeChat],
    )

    return <ChatDockContext.Provider value={value}>{children}</ChatDockContext.Provider>
}

export function useChatDock(): ChatDockContextValue {
    const context = useContext(ChatDockContext)
    if (!context) {
        throw new Error('useChatDock precisa estar dentro de ChatDockProvider')
    }
    return context
}

/**
 * O mesmo contexto, para quem pode estar FORA do provider.
 *
 * A tela de atendimento é montada em cinco lugares (tela cheia, dock, popup, aba do
 * negócio, aba do lead), e nem todo lugar que a monte amanhã estará sob o
 * `ChatDockProvider`. Lançar ali derrubaria a conversa inteira por causa de um
 * detalhe de layout.
 */
export function useChatDockOptional(): ChatDockContextValue | null {
    return useContext(ChatDockContext)
}
